import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

const API = '/api';

const MODE_COLORS = {
  FULL: "bg-[#0F6CBD] text-white",
  SHORT: "bg-[#D83B01] text-white",
  NONE: "bg-[#F3F2F1] text-[#5E5E5E]",
};

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const limit = 20;

  const fetchLogs = (skip = 0) => {
    setLoading(true);
    axios.get(`${API}/dashboard/audit-log?limit=${limit}&skip=${skip}`).then((r) => {
      setLogs(r.data.logs);
      setTotal(r.data.total);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchLogs(page * limit); }, [page]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-8 max-w-[1600px]" data-testid="audit-log-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-[#171717]">Audit Log</h1>
          <p className="text-sm text-[#5E5E5E] mt-1">
            Processing trail for all emails ({total} total)
          </p>
        </div>
        <Button
          onClick={() => fetchLogs(page * limit)}
          className="bg-[#F3F2F1] text-[#171717] hover:bg-[#E1DFDD] rounded-sm"
          data-testid="refresh-audit-btn"
        >
          <RefreshCw size={14} className="mr-2" /> Refresh
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-[#5E5E5E] mt-6">Loading audit log...</p>
      ) : logs.length === 0 ? (
        <div className="mt-8 text-center py-12 border border-dashed border-[#E1DFDD] rounded-sm">
          <p className="text-sm text-[#5E5E5E]">No emails have been processed yet.</p>
          <p className="text-xs text-[#5E5E5E] mt-1">Use the Test Processor to simulate email flows.</p>
        </div>
      ) : (
        <>
          <div className="mt-6 bg-white border border-[#E1DFDD] rounded-sm overflow-x-auto" data-testid="audit-log-table">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="text-xs uppercase bg-[#FAFAFA] text-[#171717] border-b border-[#E1DFDD]">
                  <th className="px-4 py-3 font-semibold">Timestamp</th>
                  <th className="px-4 py-3 font-semibold">From</th>
                  <th className="px-4 py-3 font-semibold">To</th>
                  <th className="px-4 py-3 font-semibold">Subject</th>
                  <th className="px-4 py-3 font-semibold">Mode</th>
                  <th className="px-4 py-3 font-semibold">Reason</th>
                  <th className="px-4 py-3 font-semibold">Confidence</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} className="border-b border-[#E1DFDD] hover:bg-[#FAFAFA] transition-colors text-[#5E5E5E]" data-testid={`audit-row-${log.id}`}>
                    <td className="px-4 py-3 text-xs whitespace-nowrap">{new Date(log.timestamp).toLocaleString()}</td>
                    <td className="px-4 py-3 font-mono text-xs">{log.from_address}</td>
                    <td className="px-4 py-3 text-xs max-w-[150px] truncate">{(log.to || []).join(", ")}</td>
                    <td className="px-4 py-3 text-xs max-w-[200px] truncate">{log.subject}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-semibold ${MODE_COLORS[log.mode] || ""}`}>
                        {log.mode}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs">{log.reason?.replace(/_/g, " ")}</td>
                    <td className="px-4 py-3 text-xs">{log.placement_confidence || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-xs text-[#5E5E5E]">
                Page {page + 1} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage(page - 1)}
                  className="border-[#E1DFDD] rounded-sm text-xs"
                  data-testid="prev-page-btn"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage(page + 1)}
                  className="border-[#E1DFDD] rounded-sm text-xs"
                  data-testid="next-page-btn"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
